// src/services/api.js
// src/services/api.js
// Support selecting API origin using a single env flag `REACT_APP_ENV` and
// multiple URL variables for each environment (local, dev, uat, qa, prod).
// Priority:
// 1. REACT_APP_API_BASE_URL (explicit override)
// 2. If REACT_APP_ENV is set and matches a known env, use its variable
// 3. If running on localhost, use local default
// 4. Fallback to dev URL or a sensible production fallback
const explicitUrl = process.env.REACT_APP_API_BASE_URL;
const envFlag = (process.env.REACT_APP_ENV || '').toLowerCase();
// Local dev: when the page is opened from another device on the network
// (e.g. http://192.168.x.x:3000), "localhost" would point at that device, so
// swap in the host the page was actually loaded from.
const localApi = (() => {
    const configured = process.env.REACT_APP_API_LOCAL || 'http://localhost:5000';
    if (typeof window === 'undefined') return configured;
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return configured;
    return configured.replace(/\/\/(localhost|127\.0\.0\.1)(?=[:/]|$)/, `//${host}`);
})();
const envMap = {
    local: localApi,
    dev: process.env.REACT_APP_API_DEV || process.env.REACT_APP_API_BASE_URL_DEV || 'https://dev-api.example.com',
    uat: process.env.REACT_APP_API_UAT || 'https://uat-api.example.com',
    qa: process.env.REACT_APP_API_QA || 'https://qa-api.example.com',
    prod: process.env.REACT_APP_API_PROD || 'https://api.example.com',
};
const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

let API_ORIGIN_FALLBACK;
if (explicitUrl) {
    API_ORIGIN_FALLBACK = explicitUrl;
} else if (envFlag && envMap[envFlag]) {
    API_ORIGIN_FALLBACK = envMap[envFlag];
} else if (isLocalhost) {
    API_ORIGIN_FALLBACK = envMap.local;
} else {
    API_ORIGIN_FALLBACK = typeof window !== 'undefined' ? window.location.origin : envMap.dev;
}

export const API_ORIGIN = API_ORIGIN_FALLBACK;
export const API_BASE_URL = `${API_ORIGIN}/api`;

// Optional global 401 handler injected by AuthContext
let onUnauthorized = null;
export const setOnUnauthorized = (fn) => { onUnauthorized = fn; };

const requestCache = new Map();

const apiRequest = async (endpoint, options = {}) => {
    const method = (options.method || 'GET').toUpperCase();
    
    // Auto-invalidate cache on any data mutation to ensure data freshness
    if (method !== 'GET') {
        requestCache.clear();
    }

    const url = `${API_BASE_URL}${endpoint}`;
    
    // Check cache for GET requests
    let cacheKey = null;
    if (method === 'GET') {
        const auth = options.headers?.Authorization || '';
        cacheKey = `${url}|${auth}`;
        const cached = requestCache.get(cacheKey);
        if (cached && (Date.now() - cached.time < 60000)) { // 60 seconds TTL
            try {
                // Return a deep copy to prevent components from mutating the cache
                return { data: JSON.parse(cached.dataStr), status: cached.status };
            } catch (e) {
                // Ignore parse errors and fall through to fetch
            }
        }
    }

    const config = {
        headers: {
            Accept: 'application/json',
            // Do not set Content-Type manually for FormData requests
            ...options.headers,
        },
        ...options,
    };

    let response;
    let data;
    try {
        response = await fetch(url, config);
        try { data = await response.json(); } catch (_) { data = {}; }
    } catch (networkErr) {
        // Re-throw AbortError so callers (e.g. 2-second timeout) can handle it
        if (networkErr && (networkErr.name === 'AbortError' || networkErr.code === 20)) {
            throw networkErr;
        }
        throw new Error('Network error. Please check your connection.');
    }

    if (!response.ok) {
        const message = data.message || data.error || `Request failed with status ${response.status}`;
        const error = new Error(message);
        error.status = response.status;
        error.data = data; // Preserve data for needsRole check
        // Trigger global handler for 401 to allow auto-logout
        if (response.status === 401 && typeof onUnauthorized === 'function') {
            try { onUnauthorized(); } catch (_) {}
        }
        throw error;
    }

    // Save successful GET requests to cache
    if (method === 'GET' && cacheKey) {
        requestCache.set(cacheKey, {
            dataStr: JSON.stringify(data),
            status: response.status,
            time: Date.now()
        });
    }

    return { data, status: response.status };
};

export const authAPI = {
    login: (email, password, role) => apiRequest('/auth/login', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(role ? { email, password, role } : { email, password }) }),
    register: (userData) => apiRequest('/auth/register', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(userData) }),
    
    getProfile: (token) => apiRequest('/auth/profile', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    getPublicProfile: (userId) => apiRequest(`/auth/users/${userId}/public`, { method: 'GET' }),

    subscribe: (token, plan, months) => apiRequest('/auth/subscribe', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ plan, months }) }),
    updateProfile: (token, payload) => {
        const isFormData = payload instanceof FormData;
        const headers = { Authorization: `Bearer ${token}` };
        if (!isFormData) headers['Content-Type'] = 'application/json';
        return apiRequest('/auth/profile', { method: 'PATCH', headers, body: isFormData ? payload : JSON.stringify(payload) });
    },
    listUsers: (token) => apiRequest('/auth/users', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    updateUserRole: (token, userId, role) => apiRequest(`/auth/users/${userId}/role`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ role }) }),
    deleteUser: (token, userId) => apiRequest(`/auth/users/${userId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }),
    requestPasswordChangeOtp: (token) => apiRequest('/auth/reset-password/request-otp', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({}) }),
    superadminSetPassword: (email, currentPassword, newPassword, confirmPassword) =>
        apiRequest('/auth/superadmin/set-password', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, currentPassword, newPassword, confirmPassword }) }),
    superadminVerifyLoginOtp: (email, otp) =>
        apiRequest('/auth/superadmin/verify-login-otp', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, otp }) }),
    resetPassword: (token, currentPassword, newPassword, confirmPassword, otp) => apiRequest('/auth/reset-password', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ currentPassword, newPassword, confirmPassword, otp }) }),
    
    // Password reset with OTP
    forgotPassword: (email) => apiRequest('/auth/forgot-password', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) }),
    verifyOTP: (email, otp) => apiRequest('/auth/verify-otp', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, otp }) }),
    resetPasswordWithOTP: (email, otp, newPassword) => apiRequest('/auth/reset-password-with-otp', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, otp, newPassword }) }),
    
    // Google OAuth endpoints
    googleVerify: (credential, role, plan) => apiRequest('/auth/google/verify', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ credential, role, plan }) }),
    unlinkGoogle: (token) => apiRequest('/auth/google/unlink', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({}) }),

    // Email verification
    verifyEmail: (token) => apiRequest(`/auth/verify-email?token=${encodeURIComponent(token)}`, { method: 'GET' }),
    resendVerification: (email) => apiRequest('/auth/resend-verification', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) }),
};

export const bankAPI = {
    getAccount: (token) => apiRequest('/bank-account', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    upsertAccount: (token, payload) => apiRequest('/bank-account', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }),
};

export const courseAPI = {
    create: (token, formData) => apiRequest('/courses', { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData }),
    listMine: (token, detailed = false, page = 1, limit = 20) => {
        const queryParams = new URLSearchParams({ page, limit });
        if (detailed) queryParams.append('detailed', '1');
        return apiRequest(`/courses?${queryParams.toString()}`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } });
    },
    updateCourse: (token, courseId, patch) => {
        const isFormData = patch instanceof FormData;
        const headers = { Authorization: `Bearer ${token}` };
        if (!isFormData) headers['Content-Type'] = 'application/json';
        return apiRequest(`/courses/${courseId}`, { method: 'PATCH', headers, body: isFormData ? patch : JSON.stringify(patch) });
    },
    browse: (token, page = 1, limit = 20) => apiRequest(`/courses/browse?page=${page}&limit=${limit}`, { method: 'GET', headers: token ? { Authorization: `Bearer ${token}` } : {} }),
    instructorCourses: (token, instructorId, page = 1, limit = 20) => apiRequest(`/courses/instructor/${instructorId}/courses?page=${page}&limit=${limit}`, { method: 'GET', headers: token ? { Authorization: `Bearer ${token}` } : {} }),
    listCart: (token) => apiRequest('/courses/cart', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    addToCart: (token, courseId) => apiRequest(`/courses/cart/${courseId}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }),
    removeFromCart: (token, courseId) => apiRequest(`/courses/cart/${courseId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }),
    enrollmentPreview: (token, courseId) => apiRequest(`/courses/enroll/${courseId}/preview`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    enrollNow: (token, courseId) => apiRequest(`/courses/enroll/${courseId}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }),
    myEnrollments: (token) => apiRequest('/courses/my-enrollments', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    myEnrollmentsDetailed: (token) => apiRequest('/courses/my-enrollments/detailed', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    courseDetail: (token, id) => apiRequest(`/courses/${id}/detail`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    videoProgress: (token, videoId, body) => apiRequest(`/courses/video/${videoId}/progress`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
    studentStats: (token) => apiRequest('/courses/student/stats', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    continueLearning: (token) => apiRequest('/courses/student/continue', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    adminListAll: (token, page = 1, limit = 20) => apiRequest(`/courses/all?page=${page}&limit=${limit}`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    adminDeleteCourse: (token, id) => apiRequest(`/courses/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }),
    updateCourseApproval: (token, id, status) => apiRequest(`/courses/${id}/approval`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) }),
    deleteCourse: (token, id) => apiRequest(`/courses/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }),
    instructorStats: (token) => apiRequest('/courses/instructor/stats', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    rateCourse: (token, courseId, rating, comment) => apiRequest(`/courses/rate/${courseId}`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ rating, comment }) }),
    listRatings: (token, courseId, page=1, size=20) => apiRequest(`/courses/${courseId}/ratings?page=${page}&size=${size}`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    resume: (token, courseId) => apiRequest(`/courses/${courseId}/resume`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    toggleVideoPreview: (token, courseId, videoId, preview) => apiRequest(`/courses/${courseId}/videos/${videoId}/preview`, { method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ preview }) }),
    finalizeCourseCreation: (token, courseId) => apiRequest(`/courses/${courseId}/finalize-creation`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }),
    // Upload videos: try S3 when available; on any failure fall back to direct backend multipart (works for large files).
    appendVideos: async (token, courseId, fileList, sectionId, sectionTitle, options = {}) => {
        const files = Array.from(fileList || []);
        const { onProgress } = options;
        if (!files.length) {
            throw new Error('No videos selected');
        }

        const nonRetryable = (err) => [401, 403, 413].includes(err?.status);

        const uploadViaBackend = async (isFallback = false) => {
            let lastResult = null;
            for (let i = 0; i < files.length; i++) {
                const f = files[i];
                const fileProgress = onProgress
                    ? (loaded, total) => onProgress(loaded, total, f, i, files.length, isFallback)
                    : undefined;
                lastResult = await new Promise((resolve, reject) => {
                    const formData = new FormData();
                    formData.append('videos', f);
                    if (sectionId != null && sectionId !== '') {
                        formData.append('sectionId', String(sectionId));
                    }
                    if (sectionTitle) {
                        formData.append('sectionTitle', sectionTitle);
                    }

                    const xhr = new XMLHttpRequest();
                    xhr.open('POST', `${API_BASE_URL}/courses/${courseId}/videos`);
                    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
                    xhr.upload.onprogress = (evt) => {
                        if (evt.lengthComputable && typeof fileProgress === 'function') {
                            fileProgress(evt.loaded, evt.total);
                        }
                    };
                    xhr.onload = () => {
                        let data = {};
                        try { data = JSON.parse(xhr.responseText || '{}'); } catch (_) { /* ignore */ }
                        if (xhr.status >= 200 && xhr.status < 300) {
                            resolve({ data, status: xhr.status });
                        } else {
                            const err = new Error(data.message || data.error || `Upload failed (${xhr.status})`);
                            err.status = xhr.status;
                            err.data = data;
                            reject(err);
                        }
                    };
                    xhr.onerror = () => reject(new Error(`Network error uploading ${f.name}`));
                    xhr.send(formData);
                });
            }
            return lastResult;
        };

        const uploadViaS3 = async () => {
            const presignBody = {
                files: files.map(f => ({
                    filename: f.name,
                    originalName: f.name,
                    contentType: f.type || 'application/octet-stream',
                    sizeBytes: f.size,
                })),
                sectionId,
                sectionTitle,
            };

            const presign = await apiRequest(`/courses/${courseId}/videos/presign`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(presignBody),
            });

            const mapped = presign.data?.files || [];
            if (!mapped.length) {
                throw new Error('No upload URLs returned');
            }

            const byName = {};
            mapped.forEach(m => {
                if (m.filename) byName[m.filename] = m;
            });

            for (let i = 0; i < files.length; i++) {
                const f = files[i];
                const entry = byName[f.name];
                if (!entry?.uploadUrl) {
                    throw new Error(`No upload URL for ${f.name}`);
                }
                const fileProgress = onProgress
                    ? (loaded, total) => onProgress(loaded, total, f, i, files.length, false)
                    : undefined;

                await new Promise((resolve, reject) => {
                    const xhr = new XMLHttpRequest();
                    xhr.open('PUT', entry.uploadUrl);
                    xhr.setRequestHeader('Content-Type', f.type || 'application/octet-stream');
                    xhr.upload.onprogress = (evt) => {
                        if (evt.lengthComputable && typeof fileProgress === 'function') {
                            fileProgress(evt.loaded, evt.total);
                        }
                    };
                    xhr.onload = () => {
                        if (xhr.status >= 200 && xhr.status < 300) resolve();
                        else reject(new Error(`S3 upload failed for ${f.name} (${xhr.status})`));
                    };
                    xhr.onerror = () => reject(new Error(`S3 upload failed for ${f.name} (network/CORS)`));
                    xhr.send(f);
                });
            }

            const completeBody = {
                items: files.map(f => {
                    const entry = byName[f.name];
                    const stored = entry?.storedFilename || entry?.filename || f.name;
                    return {
                        filename: stored,
                        storedFilename: stored,
                        originalName: f.name,
                        contentType: f.type || 'application/octet-stream',
                        sizeBytes: f.size,
                    };
                }),
                sectionId: presign.data.sectionId ?? sectionId ?? null,
                sectionTitle,
            };

            return apiRequest(`/courses/${courseId}/videos/s3-complete`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(completeBody),
            });
        };

        const preferDirect = process.env.REACT_APP_DIRECT_VIDEO_UPLOAD !== 'false' && (
            process.env.REACT_APP_DIRECT_VIDEO_UPLOAD === '1' ||
            process.env.REACT_APP_DIRECT_VIDEO_UPLOAD === 'true' ||
            !process.env.REACT_APP_USE_S3 ||
            process.env.REACT_APP_USE_S3 === 'false'
        );

        if (preferDirect) {
            return uploadViaBackend();
        }

        try {
            return await uploadViaS3();
        } catch (err) {
            if (nonRetryable(err)) throw err;
            try {
                return await uploadViaBackend(true);
            } catch (backendErr) {
                const combined = new Error(
                    `${backendErr.message || 'Direct upload failed'} (S3: ${err.message || 'failed'})`
                );
                combined.status = backendErr.status || err.status;
                combined.data = backendErr.data || err.data;
                throw combined;
            }
        }
    },
    getVideoStreamUrl: async (token, courseId, filename) => {
        const response = await apiRequest(`/courses/${courseId}/videos/${encodeURIComponent(filename)}/url`, {
            method: 'GET',
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data || response;
    },
    appendAssets: async (token, courseId, fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) {
            throw new Error('No assets selected');
        }
        const formData = new FormData();
        files.forEach(f => formData.append('assets', f));
        
        return apiRequest(`/courses/${courseId}/assets`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${token}`
            },
            body: formData
        });
    },
    deleteAsset: (token, courseId, assetId) => apiRequest(`/courses/${courseId}/assets/${assetId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }),
    deleteVideo: (token, courseId, videoId) => apiRequest(`/courses/${courseId}/videos/${videoId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }),
    updateVideoSection: (token, courseId, videoId, sectionId) => apiRequest(`/courses/${courseId}/videos/${videoId}/section`, { method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ sectionId }) }),
    createSection: (token, courseId, title, description) => apiRequest(`/courses/${courseId}/sections`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ title, description }) }),
    listSections: (token, courseId) => apiRequest(`/courses/${courseId}/sections`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    updateSection: (token, courseId, sectionId, patch) => apiRequest(`/courses/${courseId}/sections/${sectionId}`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(patch) }),
    deleteSection: (token, courseId, sectionId, reassignToSectionId) => apiRequest(`/courses/${courseId}/sections/${sectionId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(reassignToSectionId ? { reassignToSectionId } : {}) }),
    reorderSections: (token, courseId, order) => apiRequest(`/courses/${courseId}/sections/reorder`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ order }) }),
    reorderSectionItems: (token, courseId, sectionId, order, by='mapping') => apiRequest(`/courses/${courseId}/sections/${sectionId}/items/reorder`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ order, by }) }),
    instructorStudents: (token, refresh=false) => apiRequest(`/courses/instructor/students${refresh ? '?_t=' + Date.now() : ''}`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    instructorAnalytics: (token) => apiRequest('/courses/instructor/analytics', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    instructorAnalyticsDetail: (token, courseId) => apiRequest(`/courses/instructor/analytics/${courseId}`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    instructorStudentPayments: (token) => apiRequest('/courses/instructor/student-payments', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    topPerforming: (token, view='courses', weeks=8) => {
        const params = new URLSearchParams({ view, weeks: String(weeks) }).toString();
        return apiRequest(`/courses/instructor/top-performing?${params}`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } });
    },
    // Certificates & Notifications
    listCertificates: (token) => apiRequest('/courses/certificates', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    updateCertificateName: (token, certId, displayName) => apiRequest(`/courses/certificates/${certId}/name`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ displayName }) }),
    issueSessionCertificate: (token, sessionId, displayName) => apiRequest('/courses/certificates/issue-session', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId, displayName }) }),
    downloadCertificateSvg: (token, certId) => {
        // Return absolute URL for <a download>
        return `${API_BASE_URL}/courses/certificates/${certId}/svg`;
    },
    listNotifications: (token) => apiRequest(`/courses/notifications?_t=${Date.now()}`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    markNotificationRead: (token, id) => apiRequest(`/courses/notifications/${id}/read`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }),
    markAllNotificationsRead: (token) => apiRequest('/courses/notifications/read-all', { method: 'POST', headers: { Authorization: `Bearer ${token}` } }),
    // SSE Stream URL (token passed as query due to EventSource header limitation)
    notificationStreamUrl: (token) => `${API_BASE_URL}/courses/notifications/stream?token=${encodeURIComponent(token)}`,
    // Favorites (simple)
    listFavorites: (token) => apiRequest('/courses/favorites', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    addFavorite: (token, courseId) => apiRequest(`/courses/favorites/${courseId}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }),
    removeFavorite: (token, courseId) => apiRequest(`/courses/favorites/${courseId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }),
};

export const searchAPI = {
    search: (query, token) => {
        const params = new URLSearchParams({ q: query, _t: Date.now() }).toString();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        return apiRequest(`/search?${params}`, { method: 'GET', headers });
    }
};

// Live Sessions
export const liveSessionsAPI = {
    list: (token) => apiRequest(`/live-sessions?_t=${Date.now()}`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    listAll: (token) => apiRequest(`/live-sessions?scope=all&_t=${Date.now()}`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    create: (token, payload) => apiRequest('/live-sessions', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }),
    update: (token, id, payload) => apiRequest(`/live-sessions/${id}`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }),
    get: (token, id) => apiRequest(`/live-sessions/${id}?_t=${Date.now()}`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    delete: (token, id) => apiRequest(`/live-sessions/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }),
    hardDelete: (token, id) => apiRequest(`/live-sessions/${id}/hard-delete`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }),
    joinInfo: (token, id) => apiRequest(`/live-sessions/${id}/join-info`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }),
};

// Admin APIs
export const adminAPI = {
    studentPayments: (token) => apiRequest('/admin/student-payments', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    listInstructors: (token) => apiRequest('/admin/instructors', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    instructorAnalytics: (token, instructorId) => apiRequest(`/admin/instructor-analytics/${instructorId}`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    instructorAnalyticsOverall: (token) => apiRequest('/admin/instructor-analytics', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    listStudents: (token) => apiRequest('/admin/students', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    studentAnalytics: (token, studentId) => apiRequest(`/admin/student-analytics/${studentId}`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    studentAnalyticsOverall: (token) => apiRequest('/admin/student-analytics', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    overview: (token) => apiRequest('/admin/overview', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
};

// Chat APIs
export const chatAPI = {
    listThreads: (token) => apiRequest('/chat/threads', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    ensureThread: (token, courseId, studentId) => {
        const body = { courseId };
        if (studentId) body.studentId = studentId;
        return apiRequest('/chat/threads', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    },
    listMessages: (token, threadId) => apiRequest(`/chat/threads/${threadId}/messages`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    markRead: (token, threadId) => apiRequest(`/chat/threads/${threadId}/read`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }),
    listEnrolledInstructors: (token) => apiRequest('/chat/enrolled-instructors', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    listEnrolledStudents: (token) => apiRequest('/chat/enrolled-students', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
};

// Public home-page showcase (no login): hero, curated rows, stats, instructors
export const showcaseAPI = {
    get: () => apiRequest('/showcase', { method: 'GET' }),
    promoStreamUrl: (videoId) => `${API_ORIGIN}/api/showcase/promo/${videoId}/stream`,
};

// Super-admin: schedule courses into the home page featured hero
export const featuredAPI = {
    list: (token) => apiRequest('/admin/featured-courses', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    courseOptions: (token, q = '') => apiRequest(`/admin/featured-courses/course-options?q=${encodeURIComponent(q)}`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    create: (token, data) => apiRequest('/admin/featured-courses', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
    update: (token, id, data) => apiRequest(`/admin/featured-courses/${id}`, { method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
    remove: (token, id) => apiRequest(`/admin/featured-courses/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }),
};

// Subscription Plan APIs (admin management + public listing)
export const subscriptionPlanAPI = {
    list: () =>
        apiRequest('/subscription-plans', { method: 'GET' }),
    listByType: (userType) =>
        apiRequest(`/subscription-plans?user_type=${userType}`, { method: 'GET' }),
    listAll: (token) =>
        apiRequest('/subscription-plans?all=true', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    create: (token, dto) =>
        apiRequest('/subscription-plans', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(dto) }),
    update: (token, planId, dto) =>
        apiRequest(`/subscription-plans/${planId}`, { method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(dto) }),
    deactivate: (token, planId) =>
        apiRequest(`/subscription-plans/${planId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }),
};

export const accessRequestAPI = {
    submit: (payload) =>
        apiRequest('/access-requests', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }),
    checkEmail: (email) =>
        apiRequest(`/access-requests/check-email?email=${encodeURIComponent(email)}`, { method: 'GET' }),
    list: (token, params = {}) => {
        const query = new URLSearchParams(
            Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))
        ).toString();
        return apiRequest(`/access-requests${query ? `?${query}` : ''}`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } });
    },
    updateStatus: (token, id, status) =>
        apiRequest(`/access-requests/${id}`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) }),
    exportUrl: (token, params = {}) => {
        const query = new URLSearchParams(
            Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))
        );
        query.set('token', token);
        return `${API_BASE_URL}/access-requests/export?${query.toString()}`;
    },
};

// Payment APIs (Razorpay)
export const paymentAPI = {
    // Enrollment
    createEnrollmentOrder: (token, courseId) =>
        apiRequest('/payments/enrollment/create-order', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ course_id: courseId }) }),
    verifyEnrollmentPayment: (token, body) =>
        apiRequest('/payments/enrollment/verify', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),

    // Cart
    createCartOrder: (token, courseIds) => 
        apiRequest('/payments/cart/create-order', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ course_ids: courseIds }) }),
    verifyCartPayment: (token, payload) => 
        apiRequest('/payments/cart/verify', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }),


    // Subscription
    createSubscriptionOrder: (token, planKey, months) =>
        apiRequest('/payments/subscription/create-order', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ plan_key: planKey, months }) }),
    verifySubscriptionPayment: (token, body) =>
        apiRequest('/payments/subscription/verify', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),

    // History (role-scoped)
    studentHistory: (token, page = 1, limit = 10) =>
        apiRequest(`/payments/student/history?page=${page}&limit=${limit}`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    instructorHistory: (token) =>
        apiRequest('/payments/instructor/history', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    adminHistory: (token) =>
        apiRequest('/payments/admin/history', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),

    // Live Session
    createLiveSessionOrder: (token, sessionId) =>
        apiRequest('/payments/live-session/create-order', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ session_id: sessionId }) }),
    verifyLiveSessionPayment: (token, body) =>
        apiRequest('/payments/live-session/verify', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
};

export const learningScheduleAPI = {
    list: (token) =>
        apiRequest('/learning-schedules', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    create: (token, payload) =>
        apiRequest('/learning-schedules', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }),
    update: (token, scheduleId, payload) =>
        apiRequest(`/learning-schedules/${scheduleId}`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }),
    delete: (token, scheduleId) =>
        apiRequest(`/learning-schedules/${scheduleId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }),
};

// Course Q&A APIs
export const qaAPI = {
    askQuestion: (token, courseId, data) => 
        apiRequest(`/courses/${courseId}/questions`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
    listQuestions: (token, courseId, page=1, size=20) => 
        apiRequest(`/courses/${courseId}/questions?page=${page}&size=${size}`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    answerQuestion: (token, questionId, content) => 
        apiRequest(`/courses/questions/${questionId}/answers`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ content }) }),
};

// Practice Test APIs
export const testAPI = {
    updateTest: (token, testId, data) =>
        apiRequest(`/courses/tests/${testId}/update`, { method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
    deleteTest: (token, testId) =>
        apiRequest(`/courses/tests/${testId}/delete`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }),
    updateQuestion: (token, questionId, data) =>
        apiRequest(`/courses/tests/questions/${questionId}/update`, { method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
    deleteQuestion: (token, questionId) =>
        apiRequest(`/courses/tests/questions/${questionId}/delete`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }),
    createTest: (token, courseId, data) =>
        apiRequest(`/courses/${courseId}/tests`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
    listTests: (token, courseId, sectionId) => {
        let url = `/courses/${courseId}/tests`;
        if (sectionId) url += `?section_id=${sectionId}`;
        return apiRequest(url, { method: 'GET', headers: { Authorization: `Bearer ${token}` } });
    },
    getTest: (token, testId) =>
        apiRequest(`/courses/tests/${testId}`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    getTestSubmission: (token, testId) =>
        apiRequest(`/courses/tests/${testId}/my-submission`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    addQuestion: (token, testId, data) =>
        apiRequest(`/courses/tests/${testId}/questions`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
    submitTest: (token, testId, answers) =>
        apiRequest(`/courses/tests/${testId}/submit`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ answers }) }),
};

export const activityAPI = {
    getMyActivities: (token) => apiRequest('/users/me/activities', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
};
export const announcementAPI = {
    createAnnouncement: (token, data) => {
        const isFormData = data instanceof FormData;
        const headers = { Authorization: `Bearer ${token}` };
        if (!isFormData) headers['Content-Type'] = 'application/json';
        return apiRequest('/announcements', { method: 'POST', headers, body: isFormData ? data : JSON.stringify(data) });
    },
    getInstructorAnnouncements: (token, instructorId) => apiRequest(`/announcements/instructor/${instructorId}`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    getMyAnnouncements: (token) => apiRequest('/announcements/my', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    getStudentAnnouncements: (token) => apiRequest('/announcements/student', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    getInstructorDashboardAnnouncements: (token) => apiRequest(`/announcements/instructor-dashboard?t=${Date.now()}`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    updateAnnouncement: (token, id, data) => {
        const isFormData = data instanceof FormData;
        const headers = { Authorization: `Bearer ${token}` };
        if (!isFormData) headers['Content-Type'] = 'application/json';
        return apiRequest(`/announcements/${id}`, { method: 'PUT', headers, body: isFormData ? data : JSON.stringify(data) });
    },
    updateAnnouncementStatus: (token, id, status) => apiRequest(`/announcements/${id}/status`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) }),
    deleteAnnouncement: (token, id) => apiRequest(`/announcements/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }),
};

export const refundAPI = {
    requestRefund: (token, courseId, reason) => apiRequest('/refunds/request', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ course_id: courseId, reason }) }),
    myRefunds: (token) => apiRequest('/refunds/my', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    instructorPending: (token) => apiRequest('/refunds/instructor', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    instructorReview: (token, refundId, action, reason) => apiRequest(`/refunds/${refundId}/instructor-review`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ action, reason }) }),
    adminPending: (token) => apiRequest('/refunds/admin', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
    adminReview: (token, refundId, action, reason) => apiRequest(`/refunds/${refundId}/admin-review`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ action, reason }) }),
    adminAll: (token) => apiRequest('/refunds/admin/all', { method: 'GET', headers: { Authorization: `Bearer ${token}` } }),
};

